import { createTheme } from "@mui/material/styles";

// Admin look (dark sidebar, Shopify-like content). Applied on top of the app
// theme for admins only, so every MUI component on admin pages, including
// modals and menus rendered in portals, gets the same style.

const font = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

const ink = "#303030";
const subdued = "#616161";
const border = "#d4d4d4";
const divider = "#ebebeb";
const focus = "#005bd3";

// The app theme defines these Button variants with their own colours.
const primaryVariants = ["contained", "lightblue", "deepblue", "deepbluemini", "purple", "title_purple"];
const secondaryVariants = ["outlined", "purple_outlined", "white", "gray"];
const variantSelector = (variants) => variants.map((v) => `&.MuiButton-${v}`).join(", ");

const primaryButton = {
  background: ink,
  color: "#fff",
  border: 0,
  boxShadow: "inset 0 -1px 0 #000, inset 0 1px 0 rgba(255,255,255,.2)",
  "&:hover": { background: "#1a1a1a", boxShadow: "inset 0 -1px 0 #000, inset 0 1px 0 rgba(255,255,255,.2)" },
};

const secondaryButton = {
  background: "#fff",
  color: ink,
  border: 0,
  boxShadow: `0 0 0 1px ${border}, 0 1px 0 ${border}`,
  "&:hover": { background: "#f7f7f7", boxShadow: `0 0 0 1px ${border}, 0 1px 0 ${border}` },
};

const popoverPaper = {
  borderRadius: 12,
  boxShadow: "0 8px 24px rgba(0,0,0,.14), 0 0 0 1px rgba(0,0,0,.06)",
};

export const adminTheme = (outer) =>
  createTheme(outer, {
    typography: { fontFamily: font },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            fontFamily: font,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 8,
            letterSpacing: 0,
            transition: "background .12s ease, box-shadow .12s ease, transform .08s ease",
            "&:active": { transform: "scale(.98)" },
            "&:focus-visible": { outline: `2px solid ${focus}`, outlineOffset: 1 },
            [variantSelector(primaryVariants)]: primaryButton,
            [variantSelector(secondaryVariants)]: secondaryButton,
            "&.MuiButton-red": { ...primaryButton, background: "#c70a24", "&:hover": { background: "#a30b20" } },
            "&.MuiButton-text": { color: ink, "&:hover": { background: "#f1f1f1" } },
            "&.Mui-disabled": { background: "#f1f1f1", color: "#b5b5b5", boxShadow: "none" },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            color: "#4a4a4a",
            transition: "background .12s ease",
            "&:hover": { background: "#f1f1f1" },
          },
        },
      },
      MuiModal: {
        styleOverrides: {
          root: {
            // Custom modals: the content box is the root's direct child.
            "&:not(.MuiPopover-root):not(.MuiDialog-root):not(.MuiDrawer-root) > div:not(.MuiBackdrop-root):not([data-testid])": {
              border: 0,
              borderRadius: 16,
              boxShadow: "0 20px 48px rgba(0,0,0,.24), 0 0 0 1px rgba(0,0,0,.06)",
              outline: "none",
              fontFamily: font,
              animation: "usendModalIn .18s cubic-bezier(.2,.8,.2,1)",
            },
            "@keyframes usendModalIn": {
              from: { opacity: 0, scale: ".96" },
              to: { opacity: 1, scale: "1" },
            },
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            "&:not(.MuiBackdrop-invisible)": { backgroundColor: "rgba(0,0,0,.45)" },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            boxShadow: "0 20px 48px rgba(0,0,0,.24), 0 0 0 1px rgba(0,0,0,.06)",
          },
        },
      },
      MuiPopover: { styleOverrides: { paper: popoverPaper } },
      MuiMenu: {
        styleOverrides: {
          paper: popoverPaper,
          list: { padding: 6 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: font,
            fontSize: 13,
            minHeight: 32,
            borderRadius: 8,
            color: ink,
            "&:hover": { background: "#f1f1f1" },
            "&.Mui-selected, &.Mui-selected:hover": { background: divider, fontWeight: 600 },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { background: "#1a1a1a", borderRadius: 8, fontSize: 12, fontWeight: 500, padding: "6px 10px" },
          arrow: { color: "#1a1a1a" },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 40,
            padding: "8px 8px 0",
            // index.css paints every tab bar grey with !important.
            "& .MuiTabs-flexContainer": { backgroundColor: "transparent !important", borderRadius: "0 !important" },
          },
          indicator: { display: "none" },
          flexContainer: { gap: 2, alignItems: "center" },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: font,
            textTransform: "none",
            minHeight: 32,
            height: 32,
            minWidth: 0,
            padding: "0 10px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 550,
            color: subdued,
            transition: "background .12s ease, color .12s ease",
            "&:hover": { background: "#f1f1f1", color: ink },
            // The app theme gives the selected tab "12px 0" padding, which clips the pill.
            "&.Mui-selected": { background: divider, color: ink, fontWeight: 650, padding: "0 10px" },
          },
        },
      },
      MuiTableContainer: { styleOverrides: { root: { boxShadow: "none", background: "transparent" } } },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableRow-root": { background: "#f7f7f7" },
            "& .MuiTableCell-root": {
              color: subdued,
              fontWeight: 550,
              fontSize: 13,
              paddingTop: 8,
              paddingBottom: 8,
              whiteSpace: "nowrap",
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: "background .12s ease",
            "&.MuiTableRow-hover:hover, .MuiTableBody-root &:hover": { background: "#fafafa" },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { fontFamily: font, fontSize: 13, color: ink, borderBottom: `1px solid ${divider}`, padding: "8px 12px" },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: { color: "#8a8a8a", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: ink } },
        },
      },
      MuiRadio: { styleOverrides: { root: { "&.Mui-checked": { color: ink } } } },
      MuiSwitch: {
        styleOverrides: {
          switchBase: { "&.Mui-checked": { color: "#fff" }, "&.Mui-checked + .MuiSwitch-track": { backgroundColor: ink, opacity: 1 } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontFamily: font,
            fontSize: 13,
            borderRadius: 8,
            background: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#b5b5b5" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: focus, borderWidth: 2 },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { fontFamily: font, fontSize: 13, "&.Mui-focused": { color: focus } } },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            fontFamily: font,
            fontSize: 13,
            borderRadius: 8,
            "&.Mui-selected, &.Mui-selected:hover": { background: ink, color: "#fff" },
          },
        },
      },
      MuiChip: {
        styleOverrides: { root: { fontFamily: font, borderRadius: 7, fontWeight: 550, fontSize: 12 } },
      },
      MuiCircularProgress: { styleOverrides: { root: { color: ink } } },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontFamily: font,
            borderRadius: 8,
            "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": { background: ink },
          },
        },
      },
    },
  });
